import { useCallback } from 'react';
import {
  type ChatroomsInfiniteQueryData,
  getChatroomsInfiniteQueryKey,
  useUpdateChatroomsCache,
} from '#features/chatrooms';
import { useUpdateChatroomStatsInCache } from '#features/chatrooms';
import type { MessageDataObject } from '#models/services';
import { useQueryClient } from '#react-query';

type UseHandleSocketNewMessageParams = {
  currentUserId: string;
};

const emptyChatroomsInfiniteQueryKey = getChatroomsInfiniteQueryKey({ filter: {} });
const FILTERED_CHATROOMS_QUERY_INVALIDATION_MAX_AGE_MS = 30 * 1000; // 30 seconds

export const useHandleSocketNewMessage = ({ currentUserId }: UseHandleSocketNewMessageParams) => {
  const queryClient = useQueryClient();
  const updateChatroomsCache = useUpdateChatroomsCache();
  const { adjustUnreadCount } = useUpdateChatroomStatsInCache();

  return useCallback(
    (socketData: MessageDataObject) => {
      const chatroomId = socketData.chatroom;
      const isSelfMessage = socketData.author === currentUserId;

      updateChatroomsCache({ addUnreadCount: !isSelfMessage, chatroomId, newMessage: socketData });

      const emptyParamsQuery = queryClient.getQueryCache().find({
        exact: true,
        queryKey: emptyChatroomsInfiniteQueryKey,
      });

      const chatroomsInfiniteQueries = queryClient.getQueryCache().findAll({
        queryKey: getChatroomsInfiniteQueryKey({}),
      });

      for (const query of chatroomsInfiniteQueries) {
        const data = queryClient.getQueryData<ChatroomsInfiniteQueryData['data']>(query.queryKey);
        const isChatroomInCache = data?.pages.flat().some((room) => room?._id === chatroomId);

        if (isChatroomInCache) {
          continue;
        }

        const isActive = query.isActive();
        const isEmptyParamsQuery = emptyParamsQuery === query;
        const queryAgeMs = Date.now() - query.state.dataUpdatedAt;
        const isRecentlyUpdated = queryAgeMs < FILTERED_CHATROOMS_QUERY_INVALIDATION_MAX_AGE_MS;
        const shouldSkipInvalidation = isActive && !isEmptyParamsQuery && isRecentlyUpdated;

        if (shouldSkipInvalidation) {
          // Skip refetch to avoid sidebar loading on every message for a chatroom outside its cached pages; drop cache on unmount so the next use refetches.
          query.setOptions({ gcTime: 0 });
          continue;
        }

        void queryClient.invalidateQueries({ queryKey: query.queryKey });
      }

      if (!isSelfMessage) {
        adjustUnreadCount({ difference: 1 });
      }
    },
    [adjustUnreadCount, currentUserId, queryClient, updateChatroomsCache],
  );
};
